import type { footerAnimateConfig } from '@/libs/global-config';
import dayjs, { type Dayjs } from 'dayjs';

export type FooterAnimationMatcher = (jumpLink?: string | null) => boolean;

// 配置只决定哪些 tab 在什么时间窗口展示动画，不绑定具体实现方式。
export function buildFooterAnimationMatcher(
  config: footerAnimateConfig | null
): FooterAnimationMatcher | null {
  if (!config || !isFooterAnimationWindowOpen(config)) {
    return null;
  }

  const regex = createFooterAnimationPattern(config.urlPattern);
  if (!regex) {
    return null;
  }

  return (jumpLink?: string | null) => {
    if (!jumpLink) return false;
    return regex.test(jumpLink);
  };
}

function createFooterAnimationPattern(pattern: string): RegExp | null {
  if (!pattern) return null;

  try {
    return new RegExp(pattern);
  } catch {
    return null;
  }
}

function isFooterAnimationWindowOpen(config: footerAnimateConfig): boolean {
  if (!config.enabled) return false;

  const [start, end] = config.activeDateRange;
  if (!start && !end) {
    return true;
  }

  const timezoneSuffix = extractTimezoneSuffix(config.timezone);
  const now = dayjs();
  const startTime = start ? parseDateInTimezone(start, timezoneSuffix, false) : null;
  const endTime = end ? parseDateInTimezone(end, timezoneSuffix, true) : null;

  if (startTime && now.isBefore(startTime)) {
    return false;
  }

  if (endTime && now.isAfter(endTime)) {
    return false;
  }

  return true;
}

function extractTimezoneSuffix(timezone: string): string {
  const match = /^UTC([+-]\d{2}:\d{2})$/i.exec(timezone);
  if (!match) {
    return '+08:00';
  }

  return match[1];
}

function parseDateInTimezone(
  dateStr: string,
  timezoneSuffix: string,
  endOfDay: boolean
): Dayjs | null {
  if (!dateStr) return null;

  const timePart = endOfDay ? '23:59:59.999' : '00:00:00.000';
  const isoString = `${dateStr}T${timePart}${timezoneSuffix}`;
  const parsed = dayjs(isoString);

  return parsed.isValid() ? parsed : null;
}
